import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

import { hostedLoginWithAuth0 } from "@/lib/cloud-auth";
import {
  consumeHostedAuth0Callback,
  consumeHostedAuthReturnTo,
  relayHostedAuth0CallbackToPrimaryOrigin,
  relayHostedAuth0CodeCallbackToBackend,
  storeHostedAuth0Error,
  writeAuth0LoggedOutMarker,
  writeAuth0SwitchFromEmail,
} from "@/lib/auth0";
import { createHostedAuth0CallbackAttempt } from "@/lib/hosted-auth-callback";
import { PRODUCT_NAME } from "@/lib/product";

export const Route = createFileRoute("/auth/callback")({
  head: () => ({ meta: [{ title: `Completing sign-in - ${PRODUCT_NAME}` }] }),
  component: AuthCallbackPage,
});

function AuthCallbackPage() {
  const callbackAttemptRef = useRef(createHostedAuth0CallbackAttempt());
  const fallbackStartedRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    if (relayHostedAuth0CallbackToPrimaryOrigin()) return;
    if (relayHostedAuth0CodeCallbackToBackend()) return;

    const callbackAttempt = callbackAttemptRef.current;
    const callback = callbackAttempt.hasStarted() ? null : consumeHostedAuth0Callback();
    const result = callbackAttempt.claim(callback, async (claimedCallback) => {
      if (claimedCallback.error) throw new Error(claimedCallback.error);
      if (!claimedCallback.idToken) throw new Error("Auth0 did not return a sign-in token.");
      await hostedLoginWithAuth0({
        idToken: claimedCallback.idToken,
        expectedEmail: claimedCallback.expectedEmail,
        disallowedEmail: claimedCallback.disallowedEmail,
      });
    });

    if (result) {
      void result.then((outcome) => {
        if (outcome.ok) {
          writeAuth0LoggedOutMarker(false);
          writeAuth0SwitchFromEmail("");
          window.location.replace(`${window.location.origin}${consumeHostedAuthReturnTo()}`);
          return;
        }

        const message =
          outcome.error instanceof Error && outcome.error.message.trim()
            ? outcome.error.message
            : "Hosted sign-in could not be completed. Please try again.";
        storeHostedAuth0Error(message);
        window.location.replace(
          `${window.location.origin}/ui/login?auth_error=hosted_callback_failed`,
        );
      });
      return;
    }

    // Once a callback is claimed, auth bootstrap and StrictMode effect replay
    // must not start another Auth0 transaction while its exchange is pending.
    if (callbackAttempt.hasStarted() || fallbackStartedRef.current) return;

    fallbackStartedRef.current = true;
    storeHostedAuth0Error("Auth0 returned without a complete sign-in response.");
    window.location.replace(`${window.location.origin}/ui/login?auth_error=missing_callback`);
  }, []);

  return <div className="genrl-route-canvas min-h-screen w-full" aria-hidden="true" />;
}
